import {
  Configuration,
  DefaultApi,
  FemsApi,
  MasterApi,
  UserApi,
} from "@sizlcorp/sizl-api-document/dist/models";
import { CertificationApi } from "@sizlcorp/sizl-fems-api-document/dist/models/src/api/certification-api";
import { ReportApi } from "@sizlcorp/sizl-fems-api-document/dist/models/src/api/report-api";
import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from "axios";

// 환경변수에서 BASE_URL을 가져오며, 없을 경우 기본 값으로 "address" 사용
export const BASE_URL: string = process.env.REACT_APP_BASE_URL ?? "https://localhost:3000";

// Axios 인스턴스 생성
export const axiosInstance: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    // 초기 Authorization 헤더에 로컬 스토리지에서 토큰을 가져와서 설정
    Authorization: "Bearer " + (localStorage.getItem("authToken") ?? ""),
  },
  timeout: 1000 * 60 * 60 * 24 * 7, // 7일 동안의 타임아웃 설정
});

// 요청 전처리(interceptor) 설정
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // 로컬 스토리지에서 액세스 토큰을 가져옴
    const accessToken = localStorage.getItem("authToken");

    // 액세스 토큰이 존재하고, 헤더가 있다면 Authorization 헤더에 액세스 토큰을 설정
    if (accessToken && config.headers) {
      config.headers["Authorization"] = `Bearer ${accessToken}`;
    }
    // 수정된 config 반환
    return config;
  },
  (error) => {
    // 요청 중 에러 발생 시 에러 반환
    return Promise.reject(error);
  },
);

// 응답 후처리(interceptor) 설정
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    // 정상적인 응답일 경우 응답을 그대로 반환
    return response;
  },
  async (error: any) => {
    // 원래의 요청을 저장 (재시도를 위해 사용)
    const originalRequest = error.config;

    // 요청이 있고, 에러 응답이 있을 경우에만 처리
    if (originalRequest && error.response) {
      // 에러 상태가 401 (Unauthorized) 또는 403 (Forbidden)인 경우
      if (error.response.status === 401 || error.response.status === 403) {
        // 재시도 플래그를 설정하여 중복 재시도 방지
        originalRequest._retry = true;
        // 로컬 스토리지에서 refreshToken(여기선 authToken)을 가져옴
        const refreshToken = localStorage.getItem("authToken");

        if (refreshToken) {
          // 재시도할 때 Authorization 헤더에 refreshToken 설정
          originalRequest.headers["Authorization"] = `Bearer ${refreshToken}`;

          // 원래의 요청을 다시 시도하여 결과를 반환
          return axiosInstance(originalRequest);
        }
      }
    }
    // 그 외의 에러는 그대로 반환
    return Promise.reject(error);
  },
);

const userAxios = (url: string) => {
  return new UserApi(
    {
      apiKey: localStorage.getItem("authToken"),
    } as Configuration,
    url,
    axiosInstance,
  );
};

const emsAxios = (url: string) => {
  return new FemsApi(
    {
      apiKey: localStorage.getItem("authToken"),
    } as Configuration,
    url,
    axiosInstance,
  );
};

const masterAxios = (url: string) => {
  return new MasterApi(
    {
      apiKey: localStorage.getItem("authToken"),
    } as Configuration,
    url,
    axiosInstance,
  );
};

const defaultAxios = (url: string) => {
  return new DefaultApi(
    {
      apiKey: localStorage.getItem("authToken"),
    } as Configuration,
    url,
    axiosInstance,
  );
};

const certificationAxios = (url: string) => {
  return new CertificationApi(
    {
      apiKey: localStorage.getItem("authToken"),
    } as Configuration,
    url,
    axiosInstance,
  );
};

export const reportAxios = (url: string) => {
  return new ReportApi(
    {
      apiKey: localStorage.getItem("authToken"),
    } as Configuration,
    url,
    axiosInstance,
  );
};

export const UserInstance = userAxios(BASE_URL);
export const EmsInstance = emsAxios(BASE_URL);
export const MasterInstance = masterAxios(BASE_URL);
export const DefaultInstance = defaultAxios(BASE_URL);
export const CertificationInstance = certificationAxios(BASE_URL);
export const ReportInstance = reportAxios(BASE_URL);
